<template>
	<div class="vouchers-page">
		<!-- Page Header -->
		<div class="page-header">
			<h1>Voucher Administration</h1>
			<p class="subtitle">Manage and configure vouchers</p>
		</div>

		<!-- Alerts -->
		<transition name="fade">
			<div v-if="showAlert" class="alert" :class="`alert-${alertVariant}`">
				<span class="alert-message">{{ alertMessage }}</span>
				<button class="alert-close" @click="showAlert = false">✕</button>
			</div>
		</transition>

		<div class="vouchers-container">
			<!-- Vouchers List Section -->
			<AdminListSection
				title="Vouchers"
				:items="vouchers"
				:selected-id="selectedVoucher?.id"
				:loading="loading"
				search-placeholder="Search vouchers..."
				empty-message="No vouchers found"
				:search-fields="['merchant.name']"
				@select="selectVoucher"
				@refresh="refreshVouchers"
			>
				<template #item="{ item }">
					<div class="voucher-item-content">
						<div class="voucher-logo" v-if="item.merchant && item.merchant.logoUrl">
							<img :src="item.merchant.logoUrl" :alt="item.merchant.name" @error="handleImageError" />
						</div>
						<div class="voucher-info">
							<div class="voucher-name">{{ item.merchant ? item.merchant.name : 'Unknown' }}</div>
							<div class="voucher-meta">
								<span class="badge" :class="{ enabled: item.enabled, disabled: !item.enabled }">
									{{ item.enabled ? 'Enabled' : 'Disabled' }}
								</span>
								<span v-if="item.removed" class="badge removed">Removed</span>
								<span v-if="item.featured" class="badge featured">Featured</span>
							</div>
						</div>
					</div>
				</template>
			</AdminListSection>

			<!-- Voucher Details Section -->
			<div v-if="selectedVoucher" ref="voucherDetailsSection" class="voucher-details-section">
				<div class="section-header">
					<h2>{{ selectedVoucher.merchant ? selectedVoucher.merchant.name : 'Voucher Details' }}</h2>
					<button class="action-btn danger" @click="toggleReportModal">
						📊 Report
					</button>
				</div>

				<div class="details-grid">
					<!-- Basic Information -->
					<div class="form-section">
						<h3>Basic Information</h3>
						<div class="form-group">
							<label>Name</label>
							<input type="text" v-model="selectedVoucher.merchant.name" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>ID</label>
							<input type="text" v-model="selectedVoucher.id" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>Slug</label>
							<input type="text" v-model="selectedVoucher.merchant.slug" disabled class="form-input" />
						</div>
					</div>

					<!-- Configuration -->
					<div class="form-section">
						<h3>Configuration</h3>
						<div class="form-group">
							<label>Merchant</label>
							<select v-model="selectedVoucher.merchant.sysoMerchantId" @change="saveVoucher" class="form-input">
								<option :value="null">None</option>
								<option v-for="merchant in merchants" :key="merchant.id" :value="merchant.id">
									{{ merchant.name }}
								</option>
							</select>
						</div>
						<div class="form-group">
							<label>Categories</label>
							<input type="text" v-model="selectedVoucher.merchant.categories" @change="saveVoucher" class="form-input" />
						</div>
						<div class="form-group">
							<label>Delivery Method</label>
							<input type="text" v-model="selectedVoucher.deliveryMethod" disabled class="form-input" />
						</div>
					</div>

					<!-- Status Flags -->
					<div class="form-section">
						<h3>Status</h3>
						<div class="checkbox-group">
							<label class="checkbox-label">
								<input type="checkbox" v-model="selectedVoucher.enabled" :disabled="selectedVoucher.removed" @change="saveVoucher" />
								<span>Enabled</span>
							</label>
							<label class="checkbox-label">
								<input type="checkbox" v-model="selectedVoucher.removed" disabled />
								<span>Removed</span>
							</label>
							<label class="checkbox-label">
								<input type="checkbox" v-model="selectedVoucher.featured" @change="saveVoucher" />
								<span>Featured</span>
							</label>
							<label class="checkbox-label">
								<input type="checkbox" v-model="selectedVoucher.manualOverride" @change="saveVoucher" />
								<span>Manual Override</span>
							</label>
						</div>
					</div>

					<!-- Description and Terms -->
					<div class="form-section full-width">
						<h3>Description</h3>
						<textarea v-model="selectedVoucher.description" disabled class="form-textarea"></textarea>
					</div>

					<div class="form-section full-width">
						<h3>Terms</h3>
						<textarea v-model="selectedVoucher.terms" disabled class="form-textarea"></textarea>
					</div>

					<!-- Amount Configuration -->
					<div class="form-section">
						<h3>Amounts</h3>
						<div class="form-group">
							<label>Lower Amount</label>
							<input type="text" v-model="selectedVoucher.lowerAmount" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>Upper Amount</label>
							<input type="text" v-model="selectedVoucher.upperAmount" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>Allowed Amounts</label>
							<input type="text" v-model="selectedVoucher.allowedAmounts" disabled class="form-input" />
						</div>
					</div>

					<!-- Availability -->
					<div class="form-section">
						<h3>Availability</h3>
						<div class="form-group">
							<label>In Store</label>
							<input type="text" v-model="selectedVoucher.inStore" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>Online</label>
							<input type="text" v-model="selectedVoucher.online" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>Discount Percentage</label>
							<input type="text" v-model="selectedVoucher.discountPercentage" :disabled="!selectedVoucher.manualOverride" @change="saveVoucher" class="form-input" />
						</div>
					</div>

					<!-- Visual Configuration -->
					<div class="form-section">
						<h3>Visual Configuration</h3>
						<div class="form-group">
							<label>Card URL</label>
							<input type="text" v-model="selectedVoucher.cardUrl" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>Logo URL</label>
							<input type="text" v-model="selectedVoucher.merchant.logoUrl" disabled class="form-input" />
						</div>
						<div class="form-group">
							<label>Card Colour</label>
							<input type="color" v-model="selectedVoucher.cardColour" @change="saveVoucher" class="color-input" />
						</div>
					</div>

					<!-- Previews -->
					<div class="form-section full-width">
						<h3>Previews</h3>
						<div class="preview-grid">
							<div class="preview-item" v-if="selectedVoucher.cardUrl">
								<div class="preview-label">Card</div>
								<img :src="selectedVoucher.cardUrl" :alt="selectedVoucher.merchant.name" class="preview-image" @error="handleImageError" />
							</div>
							<div class="preview-item" v-if="selectedVoucher.merchant && selectedVoucher.merchant.logoUrl">
								<div class="preview-label">Logo</div>
								<img :src="selectedVoucher.merchant.logoUrl" :alt="selectedVoucher.merchant.name" class="preview-image" @error="handleImageError" />
							</div>
							<div v-if="selectedVoucher.cardColour" class="preview-item">
								<div class="preview-label">Color</div>
								<div class="color-preview" :style="{ backgroundColor: selectedVoucher.cardColour }"></div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div v-else class="empty-state-large">
				<p>Select a voucher to view details</p>
			</div>
		</div>

		<!-- Report Modal -->
		<div v-if="showReportModal" class="modal-overlay" @click="toggleReportModal">
			<div class="modal-dialog" @click.stop>
				<div class="modal-header">
					<h3>Vouchers Summary Report</h3>
					<button class="close-btn" @click="toggleReportModal">✕</button>
				</div>
				<div class="modal-body">
					<div v-if="loadingReport" class="loading">
						<div class="spinner"></div>
						<p>Loading report...</p>
					</div>
					<div v-else class="report-grid">
						<div class="report-card" v-if="discountReport">
							<h4>Discounts</h4>
							<div class="report-item">
								<span class="label">Total Vouchers Available:</span>
								<span class="value">{{ discountReport.totalVouchersAvailable }}</span>
							</div>
							<div class="report-item">
								<span class="label">Total Vouchers Purchased:</span>
								<span class="value">{{ discountReport.totalVouchersPurchased }}</span>
							</div>
							<div class="report-item">
								<span class="label">Sum Of Purchased Value:</span>
								<span class="value">£{{ discountReport.sumOfPurchasedVouchersValue }}</span>
							</div>
							<div class="report-item">
								<span class="label">Liability:</span>
								<span class="value">£{{ discountReport.liability }}</span>
							</div>
							<div class="report-item">
								<span class="label">Float Balance:</span>
								<span class="value">£{{ discountReport.floatBalance }}</span>
							</div>
						</div>

						<div class="report-card" v-if="paymentReport">
							<h4>Payments</h4>
							<div class="report-item">
								<span class="label">Total Payments Paid:</span>
								<span class="value">{{ paymentReport.totalPaymentsPaid }}</span>
							</div>
							<div class="report-item">
								<span class="label">Total Amount Paid:</span>
								<span class="value">£{{ (paymentReport.totalAmountInPencePaid / 100).toFixed(2) }}</span>
							</div>
							<div class="report-item">
								<span class="label">Total Fees Paid:</span>
								<span class="value">£{{ (paymentReport.totalFeesInPencePaid / 100).toFixed(2) }}</span>
							</div>
							<div class="report-item">
								<span class="label">Total Payments Unpaid:</span>
								<span class="value">{{ paymentReport.totalPaymentsUnpaid }}</span>
							</div>
							<div class="report-item">
								<span class="label">Total Amount Unpaid:</span>
								<span class="value">£{{ (paymentReport.totalAmountInPenceUnpaid / 100).toFixed(2) }}</span>
							</div>
							<div class="report-item">
								<span class="label">Total Fees Unpaid:</span>
								<span class="value">£{{ (paymentReport.totalFeesInPenceUnpaid / 100).toFixed(2) }}</span>
							</div>
						</div>
					</div>
				</div>
				<div class="modal-actions">
					<button class="action-btn secondary" @click="toggleReportModal">Close</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import AdminListSection from '@/admin/components/AdminListSection.vue';

export default {
	name: 'VouchersPage',
	components: {
		AdminListSection
	},
	mixins: [AdminServices],
	data() {
		return {
			vouchers: [],
			selectedVoucher: null,
			merchants: [],
			loading: false,
			showAlert: false,
			alertMessage: '',
			alertVariant: 'success',
			showReportModal: false,
			loadingReport: false,
			discountReport: null,
			paymentReport: null
		};
	},
	computed: {},
	methods: {
		refreshVouchers() {
			this.loading = true;
			this._getVouchers((data) => {
				this.vouchers = data || [];
				this.loading = false;
				if (this.vouchers.length > 0 && !this.selectedVoucher) {
					this.selectVoucher(this.vouchers[0]);
				}
			});
		},
		getMerchants() {
			this._getMerchants((data) => {
				this.merchants = data || [];
			});
		},
		selectVoucher(voucher) {
			this.selectedVoucher = JSON.parse(JSON.stringify(voucher));

			// On mobile, scroll to details section
			if (window.innerWidth <= 768) {
				this.$nextTick(() => {
					const detailsSection = this.$refs.voucherDetailsSection;
					if (detailsSection) {
						detailsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
				});
			}
		},
		saveVoucher() {
			this._updateVoucher(
				this.selectedVoucher.id,
				this.selectedVoucher,
				() => {
					this.showMessage('Voucher saved successfully');
					this.refreshVouchers();
				},
				(error) => {
					this.showMessage(error || 'Failed to save voucher', 'error');
				}
			);
		},
		toggleReportModal() {
			this.showReportModal = !this.showReportModal;
			if (this.showReportModal) {
				this.loadReport();
			}
		},
		loadReport() {
			this.loadingReport = true;
			this.discountReport = null;
			this.paymentReport = null;

			this._getDiscountReport((data) => {
				this.discountReport = data;
			});

			this._getPaymentReport((data) => {
				this.paymentReport = data;
				this.loadingReport = false;
			});
		},
		handleImageError(event) {
			event.target.style.display = 'none';
		},
		showMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			if (variant !== 'error') {
				setTimeout(() => {
					this.showAlert = false;
				}, 5000);
			}
		}
	},
	mounted() {
		this.refreshVouchers();
		this.getMerchants();
	}
};
</script>

<style scoped>
.vouchers-page {
	min-height: 100%;
	padding: 20px;
}

/* Header */
.page-header {
	margin-bottom: 30px;
}

.page-header h1 {
	margin: 0 0 5px 0;
	color: var(--admin-text-primary);
	font-size: 28px;
	font-weight: 600;
}

.subtitle {
	margin: 0;
	color: var(--admin-text-secondary);
	font-size: 14px;
}

/* Alerts */
.alert {
	margin-bottom: 20px;
	padding: 12px 15px;
	border-radius: var(--border-radius-s);
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.alert-success {
	background-color: rgba(76, 175, 80, 0.1);
	color: #2e7d32;
	border: 1px solid rgba(76, 175, 80, 0.3);
}

.alert-error {
	background-color: rgba(211, 47, 47, 0.1);
	color: #c62828;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.alert-close {
	background: none;
	border: none;
	color: inherit;
	cursor: pointer;
	font-size: 16px;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Container */
.vouchers-container {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 20px;
}

/* Vouchers List Section */
.vouchers-list-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	flex-direction: column;
	min-height: 600px;
	max-height: 80vh;
	margin-right: 10px;
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 15px;
}

.section-header h2 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: 600;
}

.search-box {
	margin-bottom: 15px;
}

.search-input {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.search-input::placeholder {
	color: var(--admin-text-secondary);
}

.vouchers-list {
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.voucher-item {
	padding: 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-hover-bg);
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
}

.voucher-item:hover {
	background-color: var(--admin-bg-secondary);
	border-color: var(--background-color-primary);
}

.voucher-item.active {
	background-color: var(--admin-bg-secondary);
	border-color: var(--background-color-primary);
	box-shadow: 0 0 0 3px rgba(51, 40, 71, 0.1);
}

.voucher-item-content {
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1;
	min-width: 0;
}

.voucher-logo {
	width: 40px;
	height: 40px;
	flex-shrink: 0;
	border-radius: var(--border-radius-s);
	overflow: hidden;
	background-color: var(--admin-bg-secondary);
	display: flex;
	align-items: center;
	justify-content: center;
}

.voucher-logo img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	padding: 2px;
}

.voucher-info {
	flex: 1;
	min-width: 0;
}

.voucher-name {
	font-weight: 500;
	color: var(--admin-text-primary);
	font-size: 13px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-bottom: 4px;
}

.voucher-meta {
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
}

.badge {
	font-size: 10px;
	padding: 2px 6px;
	border-radius: 4px;
	background-color: var(--admin-border);
	color: var(--admin-text-secondary);
}

.badge.enabled {
	background-color: rgba(76, 175, 80, 0.2);
	color: #2e7d32;
}

.badge.disabled {
	background-color: rgba(211, 47, 47, 0.2);
	color: #c62828;
}

.badge.featured {
	background-color: rgba(255, 193, 7, 0.2);
	color: #f57f17;
}

.badge.removed {
	background-color: rgba(211, 47, 47, 0.2);
	color: #c62828;
}

.voucher-count {
	font-size: 12px;
	color: var(--admin-text-secondary);
	white-space: nowrap;
	flex-shrink: 0;
	min-width: 60px;
	text-align: right;
}

/* Loading State */
.loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	text-align: center;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 4px solid var(--admin-border);
	border-top-color: var(--background-color-primary);
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
	margin-bottom: 15px;
}

@keyframes spin {
	to { transform: rotate(360deg); }
}

.loading p {
	margin: 0;
	color: var(--admin-text-secondary);
	font-size: 14px;
}

.empty-state {
	padding: 40px 20px;
	text-align: center;
	color: var(--admin-text-secondary);
	font-size: 14px;
}

.empty-state-large {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 60vh;
	color: var(--admin-text-secondary);
	font-size: 16px;
}

/* Voucher Details Section */
.voucher-details-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	max-height: 80vh;
	overflow-y: auto;
}

.details-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px;
	margin-top: 20px;
}

.form-section {
	background-color: var(--admin-hover-bg);
	padding: 15px;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
}

.form-section.full-width {
	grid-column: 1 / -1;
}

.form-section h3 {
	margin: 0 0 15px 0;
	color: var(--admin-text-primary);
	font-size: 14px;
	font-weight: 600;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 10px;
}

.form-group {
	margin-bottom: 12px;
}

.form-group:last-child {
	margin-bottom: 0;
}

.form-group label {
	display: block;
	margin-bottom: 6px;
	color: var(--admin-text-primary);
	font-size: 13px;
	font-weight: 500;
}

.form-input,
.form-textarea {
	width: 100%;
	padding: 8px 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 13px;
	font-family: inherit;
	word-break: break-word;
	overflow-wrap: break-word;
}

.form-input:disabled,
.form-textarea:disabled {
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-secondary);
	cursor: not-allowed;
}

.form-input:focus,
.form-textarea:focus {
	outline: none;
	border-color: var(--background-color-primary);
	box-shadow: 0 0 0 3px rgba(51, 40, 71, 0.1);
}

.form-textarea {
	resize: vertical;
	min-height: 80px;
}

.color-input {
	width: 60px;
	height: 38px;
	padding: 2px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	cursor: pointer;
}

.checkbox-group {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 10px;
}

@media (max-width: 480px) {
	.checkbox-group {
		grid-template-columns: 1fr;
	}
}

.checkbox-label {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	color: var(--admin-text-primary);
	font-size: 13px;
	user-select: none;
}

.checkbox-label input[type="checkbox"] {
	cursor: pointer;
}

.checkbox-label input[type="checkbox"]:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

.preview-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
	gap: 15px;
}

.preview-item {
	display: flex;
	flex-direction: column;
	align-items: center;
}

.preview-label {
	font-size: 12px;
	color: var(--admin-text-secondary);
	margin-bottom: 8px;
	font-weight: 500;
}

.preview-image {
	max-width: 100px;
	max-height: 100px;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
	padding: 4px;
	background-color: var(--admin-bg-secondary);
}

.color-preview {
	width: 100px;
	height: 80px;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
}

/* Modal */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-dialog {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	max-width: 800px;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	animation: slideIn 0.3s ease;
}

@keyframes slideIn {
	from {
		opacity: 0;
		transform: translateY(-20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid var(--admin-border);
}

.modal-header h3 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: 600;
}

.close-btn {
	background: none;
	border: none;
	color: var(--admin-text-primary);
	cursor: pointer;
	font-size: 24px;
	padding: 0;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: var(--border-radius-s);
	transition: all 0.2s ease;
}

.close-btn:hover {
	background-color: var(--admin-hover-bg);
}

.modal-body {
	flex: 1;
	overflow-y: auto;
	padding: 20px;
}

.report-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 20px;
}

.report-card {
	background-color: var(--admin-hover-bg);
	padding: 15px;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
}

.report-card h4 {
	margin: 0 0 15px 0;
	color: var(--admin-text-primary);
	font-size: 14px;
	font-weight: 600;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 10px;
}

.report-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 0;
	border-bottom: 1px solid var(--admin-border);
	font-size: 13px;
}

.report-item:last-child {
	border-bottom: none;
}

.report-item .label {
	color: var(--admin-text-secondary);
	flex: 1;
}

.report-item .value {
	color: var(--admin-text-primary);
	font-weight: 500;
	text-align: right;
}

.modal-actions {
	display: flex;
	gap: 10px;
	padding: 15px 20px;
	border-top: 1px solid var(--admin-border);
	justify-content: flex-end;
}

/* Buttons */
.action-btn {
	padding: 8px 16px;
	border: none;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.action-btn.primary {
	background-color: var(--background-color-primary);
	color: white;
}

.action-btn.primary:hover:not(:disabled) {
	opacity: 0.9;
	transform: translateY(-1px);
}

.action-btn.secondary {
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	border: 1px solid var(--admin-border);
}

.action-btn.secondary:hover:not(:disabled) {
	background-color: var(--admin-bg-secondary);
}

.action-btn.danger {
	background-color: rgba(211, 47, 47, 0.1);
	color: #d32f2f;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.action-btn.danger:hover:not(:disabled) {
	background-color: #d32f2f;
	color: white;
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Responsive */
@media (max-width: 1200px) {
	.vouchers-container {
		grid-template-columns: 250px 1fr;
	}

	.details-grid {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 768px) {
	.vouchers-page {
		padding: 15px;
		padding-left: 10px;
	}

	.vouchers-container {
		grid-template-columns: 1fr;
	}

	.vouchers-list-section {
		max-height: 250px;
		margin-bottom: 20px;
		margin-right: 0;
		padding: 15px;
	}

	.section-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;
	}

	.section-header h2 {
		font-size: 16px;
	}

	.details-grid {
		grid-template-columns: 1fr;
		gap: 15px;
	}

	.page-header h1 {
		font-size: 24px;
	}

	.modal-dialog {
		max-width: 95vw;
		max-height: 95vh;
		margin: 10px;
	}

	.modal-body {
		padding: 15px;
	}

	.report-grid {
		grid-template-columns: 1fr;
	}

	.form-section {
		padding: 12px;
	}

	.form-section h3 {
		font-size: 13px;
		margin-bottom: 12px;
		padding-bottom: 8px;
	}

	.form-group {
		margin-bottom: 10px;
	}

	.form-group label {
		font-size: 12px;
		margin-bottom: 4px;
	}

	.form-input,
	.form-textarea {
		padding: 6px 8px;
		font-size: 12px;
	}

	.form-textarea {
		min-height: 60px;
	}

	.checkbox-group {
		gap: 8px;
	}

	.checkbox-label {
		font-size: 12px;
	}

	.preview-grid {
		grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
		gap: 10px;
	}

	.preview-image {
		max-width: 80px;
		max-height: 80px;
	}

	.color-preview {
		width: 80px;
		height: 60px;
	}

	.modal-header {
		padding: 15px;
	}

	.modal-header h3 {
		font-size: 16px;
	}
}

@media (max-width: 480px) {
	.vouchers-page {
		padding: 15px;
		padding-left: 10px;
	}

	.vouchers-container {
		grid-template-columns: 1fr;
	}

	.page-header {
		margin-bottom: 20px;
	}

	.page-header h1 {
		font-size: 20px;
	}

	.subtitle {
		font-size: 12px;
	}

	.vouchers-list-section {
		max-height: 350px;
		margin-bottom: 15px;
		margin-right: 0;
		padding: 12px;
	}

	.search-input {
		padding: 8px 10px;
		font-size: 12px;
	}

	.voucher-item {
		padding: 12px;
		gap: 8px;
	}

	.voucher-logo {
		width: 36px;
		height: 36px;
	}

	.voucher-name {
		font-size: 12px;
	}

	.voucher-count {
		font-size: 11px;
		min-width: 50px;
	}

	.badge {
		font-size: 9px;
		padding: 2px 4px;
	}

	.section-header {
		flex-direction: column;
		gap: 8px;
	}

	.section-header h2 {
		font-size: 14px;
	}

	.action-btn {
		padding: 6px 12px;
		font-size: 12px;
	}

	.details-grid {
		gap: 12px;
	}

	.form-section {
		padding: 12px;
	}

	.form-section h3 {
		font-size: 12px;
		margin-bottom: 10px;
		padding-bottom: 6px;
	}

	.form-group {
		margin-bottom: 8px;
	}

	.form-group label {
		font-size: 11px;
		margin-bottom: 3px;
	}

	.form-input,
	.form-textarea {
		padding: 5px 8px;
		font-size: 11px;
	}

	.form-textarea {
		min-height: 50px;
	}

	.color-input {
		width: 50px;
		height: 32px;
	}

	.checkbox-group {
		grid-template-columns: 1fr;
		gap: 6px;
	}

	.checkbox-label {
		font-size: 11px;
		gap: 6px;
	}

	.preview-grid {
		grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
		gap: 8px;
	}

	.preview-item {
		gap: 4px;
	}

	.preview-label {
		font-size: 10px;
		margin-bottom: 4px;
	}

	.preview-image {
		max-width: 60px;
		max-height: 60px;
		padding: 2px;
	}

	.color-preview {
		width: 60px;
		height: 50px;
	}

	.modal-dialog {
		max-width: 90vw;
		margin: 5px;
	}

	.modal-header {
		padding: 12px;
		gap: 8px;
	}

	.modal-header h3 {
		font-size: 14px;
	}

	.close-btn {
		font-size: 20px;
		width: 28px;
		height: 28px;
	}

	.modal-body {
		padding: 12px;
	}

	.report-card {
		padding: 12px;
	}

	.report-card h4 {
		font-size: 13px;
		margin-bottom: 12px;
	}

	.report-item {
		font-size: 11px;
		padding: 6px 0;
	}

	.action-link {
		width: 24px;
		height: 24px;
		font-size: 12px;
	}

	.modal-actions {
		padding: 10px 12px;
		gap: 8px;
	}
}

/* Light mode overrides - REQUIRED for all admin pages */
:root.light-mode .vouchers-list-section {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .vouchers-page {
	background-color: #f5f5f5 !important;
}

:root.light-mode .voucher-item {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .voucher-item:hover {
	background-color: #f9f9f9 !important;
	border-color: #d0d0d0 !important;
}

:root.light-mode .voucher-item.active {
	background-color: #f0f0f0 !important;
	border-color: var(--background-color-alt6) !important;
	box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2) !important;
}

:root.light-mode .voucher-details-section {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .form-section {
	background-color: #f9f9f9 !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .form-section h3 {
	border-bottom-color: #e0e0e0 !important;
}

:root.light-mode .search-input {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
	color: var(--admin-text-primary) !important;
}

:root.light-mode .modal-dialog {
	background-color: #ffffff !important;
}

:root.light-mode .modal-header {
	border-bottom-color: #e0e0e0 !important;
	background-color: #ffffff !important;
}

:root.light-mode .modal-body {
	background-color: #ffffff !important;
}

:root.light-mode .modal-footer {
	border-top-color: #e0e0e0 !important;
	background-color: #f9f9f9 !important;
}

:root.light-mode .report-card {
	background-color: #f9f9f9 !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .report-card h4 {
	border-bottom-color: #e0e0e0 !important;
}

:root.light-mode .report-item {
	border-bottom-color: #e0e0e0 !important;
}

:root.light-mode .alert {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}
</style>
